import { Injectable, signal } from '@angular/core';
import Konva from 'konva';
import { lastValueFrom } from 'rxjs';

// models
import { Bounding } from '../../core/models/bounding';
import { BoxElement } from '../models/elements/boxElement';
import { CopyingData } from '../../core/models/copyingData';
import { CopyingNotification } from '../../core/models/copyingNotification';
import { Element } from '../../equipment/models/elements/element';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { ExtensionType } from '../../equipment/models/elements/extension';
import { Figure } from '../../equipment/models/figures/figure';
import { HangBarElement } from '../models/elements/hangBarElement';
import { HoleElement } from '../models/elements/holeElement';
import { HookElement } from '../models/elements/hookElement';
import { EquipmentBase } from '../models/equipment-base';
import { IPlanData } from '../../plan/models/planData';
import { IPlanogram } from '../models/planogram';
import { IPlanogramBlockData, IPlanogramData, IPlanogramPlanData, IPlanogramProductData } from '../models/planogramData';
import { IProductConf } from '../../product/models/models';
import { PalletElement } from '../models/elements/palletElement';
import { Product, ProductParams } from '../models/product';
import { ProductGroup } from '../models/product-group';
import { IProductPacking } from '../../product/models/product-card';
import { ProductGroupRotation } from '../models/product-group-rotation';
import { RotationConfiguration } from '../../core/models/rotationConfiguration';
import { RotationConfigurationData } from '../../core/models/rotationConfigurationData';
import { RotationData } from '../../core/models/rotationData';
import { RotationNotification } from '../../core/models/rotationNotification';
import { ShelfElement } from '../models/elements/shelfElement';
import { StandConf } from '../../core/services/planograms.service';

// services
import { EquipmentService } from '../../core/services/equipment.service';
import { LayoutService } from '../../core/services/layout.service';
import { PlanogramsService } from '../../core/services/planograms.service';
import { EMPTY_PRODUCT_IMAGE_PATH, ProductsService } from '../../core/services/products.service';
import { SessionService } from '../../core/services/session.service';
import { Utils } from '../../core/services/utils.service';

const NORMALIZABLE: Array<any> = [BoxElement, HookElement, HoleElement, PalletElement, ShelfElement, HangBarElement],
	SHARE_ERRORS: { [key: string]: string } = { STOCK_QTY: 'STOCK_ERROR_', QTY: 'SALES_ERROR_' };

@Injectable({
	providedIn: 'root'
})
export class PlanogramLayoutService {

	private static checkFreeHooks(
		productRatings: { [key: string]: number },
		products: Array<ProductGroup>,
		maxLength: number
	): { [key: string]: Array<ProductGroup> } {
		const total: number = Utils.sum(Object.values(productRatings)),
			copiedProductsOnElements: { [key: string]: Array<ProductGroup> } = {};

		for (let id in productRatings) {
			productRatings[id] /= total;
		}

		for (let id in productRatings) {
			const counter: number = Math.floor(productRatings[id] * maxLength),
				product: ProductGroup = products.find((pg: ProductGroup) => pg.product.id == Number(id));

			if (counter < 0 || isNaN(counter)) continue;

			copiedProductsOnElements[id] = Array.from(new Array(counter)).map((_) => product.copy(false, false));
		}

		let currentSum: number = Utils.sum(
			Object.values(copiedProductsOnElements).map((group: Array<ProductGroup>) => group.length)
		);

		if (!currentSum) return copiedProductsOnElements;

		while (currentSum < maxLength) {
			const sortedRatings: Array<string> = Object.keys(productRatings)
				.sort((a: string, b: string) => productRatings[b] - productRatings[a]);

			for (let id of sortedRatings) {
				const product: ProductGroup = products.find((pg: ProductGroup) => pg.product.id == Number(id));

				copiedProductsOnElements[id].push(product);
				currentSum++;

				if (currentSum >= maxLength) break;
			}
		}

		return copiedProductsOnElements;
	}

	private static checkShelfFreeSpace(
		targetShelfProducts: Array<ProductGroup>,
		targetSpace: number,
		hierarchy: Array<string>
	): void {
		const min: number = Math.min(...targetShelfProducts.map((pg: ProductGroup) => pg.ItemWidth));

		let diff: number = Utils.round(targetSpace - targetShelfProducts.filter((pg: ProductGroup) => !pg.dependProduct).reduce((a: number, b: ProductGroup) => a + b.Width, 0)),
			maxIterations: number = 100,
			iterationCount: number = 0;

		while (diff >= min) {
			iterationCount++;

			for (let id of hierarchy) {
				const product: ProductGroup = targetShelfProducts.find((pg: ProductGroup) => pg.product.id == Number(id));

				if (!product) continue;

				if (product.dependProduct) continue;

				if (product.ItemWidth > diff) continue;

				product.FacesQty += 1;

				diff = Utils.round(diff - product.ItemWidth);

				if (diff >= min) continue;
				break;
			}

			if (iterationCount === maxIterations) break;
		}
	}

	private static filterBaseShelves(element: Element, base: Element): boolean {
		return element.is(ShelfElement, PalletElement) && (element.getDepends().includes(base) || element == base);
	}

	private static filterBaseHangBars(element: Element, base: Element): boolean {
		return element.is(HangBarElement) && element.getDepends().includes(base);
	}

	private static filterBaseHooks(element: Element, base: Element): boolean {
		return element.is(HookElement) && element.getDepends().includes(base);
	}

	private static filterBaseHoles(element: Element, base: Element): boolean {
		return element.is(HoleElement) && element.getDepends().includes(base);
	}

	private static moveBoxProducts(element: Element, newElement: Element, products: Array<Array<ProductGroup>>): void {
		for (let groupIndex: number = 0; groupIndex < products.length; groupIndex++) {

			const commonProduct: ProductGroup = products[groupIndex][0],
				deltaX: number = commonProduct.begin.x - commonProduct.getBoundingX().max,
				x: number = newElement.getBoundingX().max -
					newElement.shapes[0].wallWidth -
					(commonProduct.getBoundingX().min - element.getBoundingX().min - element.shapes[0].wallWidth) +
					deltaX;

			for (let productIndex: number = 0; productIndex < products[groupIndex].length; productIndex++) {
				const product: ProductGroup = products[groupIndex][productIndex];

				// keep the depth if it is for the entire box
				if (element.getMaxPlacementDepth(product) === product.placementDepth) {
					const oldFacesQty: number = product.FacesQty;
					product.placementDepth = newElement.getMaxPlacementDepth(product);
					product.qty = product.placementDepth * oldFacesQty;
				}
				product.element = newElement;
				product.begin.x = x;
			}
		}
	}

	private static moveHookProducts(element: Element, newElement: Element, products: Array<Array<ProductGroup>>): void {
		for (let groupIndex: number = 0; groupIndex < products.length; groupIndex++) {
			for (let productIndex: number = 0; productIndex < products[groupIndex].length; productIndex++) {
				const product: ProductGroup = products[groupIndex][productIndex];

				// keep the depth if it is for the entire hook
				if (element.getMaxPlacementDepth(product) === product.placementDepth) {
					product.placementDepth = newElement.getMaxPlacementDepth(product);
					product.qty = product.placementDepth;
				}
				product.element = newElement;
				product.begin.y = newElement.getBoundingY().min - product.ItemHeight;
				product.begin.x = newElement.shapes[0].begin.x - (product.ItemWidth - newElement.shapes[0].width) / 2;
			}
		}
	}

	private static sortElements(a: Element, b: Element): number {
		return b.getBoundingY().min - a.getBoundingY().min || a.getBoundingX().min - b.getBoundingX().min;
	}

	private static sortProducts(a: ProductGroup, b: ProductGroup): number {
		return b.getBoundingY().min - a.getBoundingY().min || a.getBoundingX().min - b.getBoundingX().min;
	}

	constructor(
		private equipmentService: EquipmentService,
		private layoutService: LayoutService,
		private planogramsService: PlanogramsService,
		private productsService: ProductsService,
		private sessionService: SessionService
	) {}

	public async copyPlanogramSimple(
		planograms: Array<IPlanogram>,
		planogramFrom: IPlanogram,
		result: CopyingData
	): Promise<Array<CopyingNotification>> {
		const configurations: Array<StandConf> = [],
			planogramErrors: { [key: string]: Array<{ shelf: number }> } = {},
			errorIndicators: Array<any> = [];

		planograms.forEach((planogram: IPlanogram) => {
			planogram.stand.products.length = 0;
			planogramErrors[planogram.id] = [];

			const currentBases: Array<Element> = planogramFrom.stand.bases
					.sort((a: Element, b: Element) => a.shapes[0].begin.x - b.shapes[0].begin.x),
				targetBases: Array<Element> = planogram.stand.bases
					.sort((a: Element, b: Element) => a.shapes[0].begin.x - b.shapes[0].begin.x);

			currentBases.forEach((base: Element, idx: number) => {
				if (!targetBases[idx]) return;

				const baseShelves: Array<Element> = planogramFrom.stand.availableElements
						.filter((element: Element) => PlanogramLayoutService.filterBaseShelves(element, base)),
					groupedProducts: Array<Array<ProductGroup>> = baseShelves
						.sort((a: Element, b: Element) => a.shapes[0].begin.y - b.shapes[0].begin.y)
						.map((shelf: Element) => {
							const boxElements: Array<Element> = planogramFrom.stand.availableElements
								.filter((element: Element) => element.is(BoxElement) && element.depend == shelf);
							const boxProducts: Array<ProductGroup> = Utils
								.to1DArray(boxElements.map((b: Element) => b.products))
								.filter((pg: ProductGroup) => !pg.isLooseForm);
							const totalProducts: Array<ProductGroup> = Utils
								.to1DArray([...shelf.products, ...boxProducts]);
							const copiedProducts: Array<ProductGroup> = totalProducts
								.map((pg: ProductGroup) => pg.copy(false, false));

							copiedProducts.forEach((pg: ProductGroup, idx: number, list: Array<ProductGroup>) => {
								if (totalProducts[idx].dependProduct) {
									const dependIdx: number = totalProducts.indexOf(totalProducts[idx].dependProduct)
									pg.dependProduct = list[dependIdx];
								}
							});

							return copiedProducts;
						})
						.filter((group: Array<ProductGroup>) => group.length),
					productsOnHangBars: Array<ProductGroup> = planogramFrom.stand.products
						.filter((pg: ProductGroup) => PlanogramLayoutService.filterBaseHangBars(pg.element, base))
						.map((pg: ProductGroup) => pg.copy(false, false))
						.sort(PlanogramLayoutService.sortProducts),
					productsOnHooks: Array<ProductGroup> = planogramFrom.stand.products
						.filter((pg: ProductGroup) => PlanogramLayoutService.filterBaseHooks(pg.element, base))
						.map((pg: ProductGroup) => pg.copy(false, false))
						.sort(PlanogramLayoutService.sortProducts),
					productsOnHoles: Array<ProductGroup> = planogramFrom.stand.products
						.filter((pg: ProductGroup) => PlanogramLayoutService.filterBaseHoles(pg.element, base))
						.map((pg: ProductGroup) => pg.copy(false, false))
						.sort(PlanogramLayoutService.sortProducts),
					targetShelves: Array<Element> = planogram.stand.availableElements
						.filter((element: Element) => PlanogramLayoutService.filterBaseShelves(element, targetBases[idx]))
						.sort((a: Element, b: Element) => a.shapes[0].begin.y - b.shapes[0].begin.y),
					targetHangBars: Array<Element> = planogram.stand.availableElements
						.filter((element: Element) => PlanogramLayoutService.filterBaseHangBars(element, targetBases[idx]))
						.sort((a: Element, b: Element) => a.shapes[0].begin.y - b.shapes[0].begin.y),
					targetHooks: Array<Element> = planogram.stand.availableElements
						.filter((element: Element) => PlanogramLayoutService.filterBaseHooks(element, targetBases[idx]))
						.sort(PlanogramLayoutService.sortElements),
					targetHoles: Array<Element> = planogram.stand.availableElements
						.filter((element: Element) => PlanogramLayoutService.filterBaseHoles(element, targetBases[idx]))
						.sort(PlanogramLayoutService.sortElements),
					shelvesIndexes: Array<number> = groupedProducts.map((group: Array<ProductGroup>) => {
						return planogramFrom.stand.availableElements
							.filter((element: Element) => PlanogramLayoutService.filterBaseShelves(element, base))
							.sort((a: Element, b: Element) => a.shapes[0].begin.y - b.shapes[0].begin.y)
							.indexOf(group[0].element.is(BoxElement) ? group[0].element.depend : group[0].element);
					}),
					hangBarsIndexes: Array<number> = productsOnHangBars.map((pg: ProductGroup) => {
						return planogramFrom.stand.availableElements
							.filter((element: Element) => PlanogramLayoutService.filterBaseHangBars(element, base))
							.sort((a: Element, b: Element) => a.shapes[0].begin.y - b.shapes[0].begin.y)
							.indexOf(pg.element);
					}),
					hooksIndexes: Array<number> = productsOnHooks.map((pg: ProductGroup) => {
						return planogramFrom.stand.availableElements
							.filter((element: Element) => PlanogramLayoutService.filterBaseHooks(element, base))
							.sort(PlanogramLayoutService.sortElements)
							.indexOf(pg.element);
					}),
					holesIndexes: Array<number> = productsOnHoles.map((pg: ProductGroup) => {
						return planogramFrom.stand.availableElements
							.filter((element: Element) => PlanogramLayoutService.filterBaseHoles(element, base))
							.sort(PlanogramLayoutService.sortElements)
							.indexOf(pg.element);
					}),
					targetProducts: Array<Array<ProductGroup>> = groupedProducts
						.filter((group: Array<ProductGroup>, idx: number) => targetShelves[shelvesIndexes[idx]]),
					copiedHangBarProducts: Array<ProductGroup> = [],
					copiedHookProducts: Array<ProductGroup> = [],
					copiedHolesProducts: Array<ProductGroup> = [];

				targetProducts.forEach((productsOnShelf: Array<ProductGroup>, idx: number) => {
					productsOnShelf.forEach((pg: ProductGroup) => {
						const shelf: Element = targetShelves[shelvesIndexes[idx]],
							elementsOnShelf: Array<Element> = shelf.is(ShelfElement)
								? (shelf as ShelfElement).getElementsOnShelf
								: [],
							sidesWall: Element = elementsOnShelf
								.find((element: Element) => element.parent.type == EquipmentElements.SideWalls),
							partitions: Array<Element> = elementsOnShelf
								.filter((element: Element) => element.parent.type == 'Partition');

						if (sidesWall) pg.begin.x += sidesWall.shapes[0].width;

						if (partitions.length) {
							const sidesWallPartition: Element = partitions
								.find((element: Element) => element.getBoundingX().min == shelf.getBoundingX().min);
							if (sidesWallPartition) pg.begin.x += sidesWallPartition.shapes[0].width;
						}
						pg.begin.y = shelf.shapes[0].begin.y + shelf.shapes[0].height + (pg.dependProduct ? pg.dependProduct.getBoundingY().max - pg.dependProduct.element.getBoundingY().max : 0);
						pg.element = shelf;
					});
				});

				productsOnHangBars.forEach((pg: ProductGroup, idx: number) => {
					if (!targetHangBars[hangBarsIndexes[idx]]) return;

					pg.Element = targetHangBars[hangBarsIndexes[idx]];

					copiedHangBarProducts.push(pg);
				});

				productsOnHooks.forEach((pg: ProductGroup, idx: number) => {
					if (!targetHooks[hooksIndexes[idx]]) return;

					pg.Element = targetHooks[hooksIndexes[idx]];

					copiedHookProducts.push(pg);
				});

				productsOnHoles.forEach((pg: ProductGroup, idx: number) => {
					if (!targetHoles[holesIndexes[idx]]) return;

					pg.Element = targetHoles[holesIndexes[idx]];

					copiedHolesProducts.push(pg);
				});

				planogram.stand.products.push(...Utils.to1DArray(targetProducts));
				planogram.stand.products.push(...copiedHangBarProducts);
				planogram.stand.products.push(...copiedHookProducts);
				planogram.stand.products.push(...copiedHolesProducts);
			});

			if (result.isReversed && !planogram.stand.blocks?.length) {
				planogram.stand = this.getReversed(planogram.stand);
			}

			planogram.stand.availableElements.forEach((element: Element) => {
				if (element.is(...NORMALIZABLE)) element.normalize(planogram.stand);
			});

			planogram.stand.products.forEach((pg: ProductGroup, idx: number) => {
				if (pg.error) planogramErrors[planogram.id].push({ shelf: idx });
			});

			const products: Array<IProductConf> = this.productsService
					.getProductsToConf(planogram.id, planogram.stand.products),
				conf: StandConf = this.planogramsService.getConf(planogram, products, {
					id: planogram.id,
					main_stand: planogram.main_stand,
					blocks: [],
					conf: {
						products: planogramFrom.conf.products.map((p: any) => {
							return { id: p.id, planedPercent: p.planedPercent };
						}),
						reportParams: planogramFrom.reportParams
					}
				});

			configurations.push(conf);
		});

		await lastValueFrom(this.planogramsService.bulkUpdate(configurations));

		await this.planogramsService.makeSvg(this.planogramsService.getDefaultSvgConf(planograms));

		Object.keys(planogramErrors)
			.filter((key: string) => planogramErrors[key]?.length)
			.forEach((key: string) => {
				errorIndicators.push({
					planogram: Number(key),
					name: 'PLANOGRAM_COPY',
					type: 'ERROR'
				});
			});

		if (errorIndicators.length) this.planogramsService.createIndicators(errorIndicators).subscribe();

		return result.planograms.map((data: { planogram_id: number, plan_id: number }) => {
			const planogram: IPlanogram = planograms.find((p: IPlanogram) => p.main_stand == data.planogram_id);
			return {
				id: planogram.id,
				name: planogram.name,
				errors: [],
				stand_errors: planogramErrors[planogram.id],
				shop: result.library.find((lib) => lib.planogram_id == data.plan_id)?.planogram_name,
				planogram_id: data.plan_id,
				deniedReversed: false
			};
		});
	}

	public async copyPlanogramSmart(
		planograms: Array<IPlanogram>,
		planogramFrom: IPlanogram,
		result: CopyingData
	): Promise<Array<CopyingNotification>> {
		const currentBases: Array<Element> = planogramFrom.stand.bases
				.sort((a: Element, b: Element) => a.shapes[0].begin.x - b.shapes[0].begin.x),
			originalGroupedProductsByShelf: Array<Array<Array<ProductGroup>>> = currentBases.map((base: Element) => {
				const baseShelves: Array<Element> = planogramFrom.stand.availableElements
					.filter((element: Element) => PlanogramLayoutService.filterBaseShelves(element, base));

				return baseShelves
					.sort((a: Element, b: Element) => a.shapes[0].begin.y - b.shapes[0].begin.y)
					.map((shelf: Element) => {
						const boxElements: Array<Element> = planogramFrom.stand.availableElements
							.filter((element: Element) => element.is(BoxElement) && element.depend == shelf);
						const boxProducts: Array<ProductGroup> = Utils
							.to1DArray(boxElements.map((b: Element) => b.products))
							.filter((pg: ProductGroup) => !pg.isLooseForm);
						const totalProducts: Array<ProductGroup> = Utils
							.to1DArray([...shelf.products, ...boxProducts]);
						const copiedProducts: Array<ProductGroup> = totalProducts
							.map((pg: ProductGroup) => pg.copy(false, false));

						copiedProducts.forEach((pg: ProductGroup, idx: number, list: Array<ProductGroup>) => {
							if (totalProducts[idx].dependProduct) {
								const dependIdx: number = totalProducts.indexOf(totalProducts[idx].dependProduct)
								pg.dependProduct = list[dependIdx];
							}
						});

						return copiedProducts;
					});
			}),
			originalGroupedProductsByHangBar: Array<Array<Array<ProductGroup>>> = currentBases.map((base: Element) => {
				const baseHangBars: Array<Element> = planogramFrom.stand.availableElements
					.filter((element: Element) => PlanogramLayoutService.filterBaseHangBars(element, base));

				return baseHangBars
					.sort((a: Element, b: Element) => a.shapes[0].begin.y - b.shapes[0].begin.y)
					.map((hangBar: Element) => {
						return hangBar.products.flat().map((pg: ProductGroup) => pg.copy(false, false));
					});
			}),
			originalGroupedProductsByHooks: Array<ProductGroup> = planogramFrom.stand.products
				.filter((pg: ProductGroup) => pg.element.is(HookElement))
				.sort(PlanogramLayoutService.sortProducts),
			hookProductsQty = Utils.groupBy(originalGroupedProductsByHooks, 'product')
				.reduce((a, b) => ({ ...a, [b[0].product.id]: b.length }), {}),
			totalHooksQty: number = Object.values<number>(hookProductsQty).reduce((a: number, b: number) => a + b, 0),
			originalGroupedProductsByHoles: Array<ProductGroup> = planogramFrom.stand.products
				.filter((pg: ProductGroup) => pg.element.is(HoleElement))
				.sort(PlanogramLayoutService.sortProducts),
			holeProductsQty = Utils.groupBy(originalGroupedProductsByHoles, 'product')
				.reduce((a, b) => ({ ...a, [b[0].product.id]: b.length }), {}),
			totalHolesQty: number = Object.values<number>(holeProductsQty).reduce((a: number, b: number) => a + b, 0),
			configurations: Array<StandConf> = [],
			productsErrors: { [key: string]: Array<{ name: string; reason: string; }> } = {},
			planogramErrors: { [key: string]: Array<{ shelf: number }> } = {},
			errorIndicators: Array<any> = [],
			predictedData: any = {};

		switch (result.type) {
			case 'QTY':
				Object.assign(
					predictedData,
					await this.productsService.getProductsPercent(
						result.date_filters,
						planogramFrom.stand.products.map((i) => i.product.id),
						planogramFrom.planogram_id,
						planogramFrom.main_stand
					)
				);
				break;

			case 'STOCK_QTY':
				Object.assign(
					predictedData,
					await this.productsService.getProductsStock(
						planogramFrom.stand.products.map((i) => i.product.id),
						planogramFrom.planogram_id,
						result.date_filters
					)
				);
				break;
		}

		for (let planogram of planograms) {
			planogram.stand.products.length = 0;
			productsErrors[planogram.id] = [];
			planogramErrors[planogram.id] = [];

			const targetBases: Array<Element> = planogram.stand.bases
					.sort((a: Element, b: Element) => a.shapes[0].begin.x - b.shapes[0].begin.x),
				targetMinShelvesNumber: number = Math.min(
					...planogram.stand.bases
						.map((base: Element) => planogram.stand.availableElements
							.filter((element: Element) => PlanogramLayoutService.filterBaseShelves(element, base)).length)
				),
				targetShelves: Array<Array<Element>> = targetBases
					.map((base: Element) =>
						planogram.stand.availableElements
							.filter((element: Element) => PlanogramLayoutService.filterBaseShelves(element, base))
							.sort((a: Element, b: Element) => a.shapes[0].begin.y - b.shapes[0].begin.y)
					)
					.map((elements: Array<Element>) => {
						elements.length = targetMinShelvesNumber;
						return elements.filter((element: Element) => element);
					}),
				targetShelvesTransposed: Array<Array<Element>> = Utils.transposeVariableLengthArray(targetShelves),
				groupedProductsByTargetShelves: Array<Array<Array<ProductGroup>>> = originalGroupedProductsByShelf
					.map((group: Array<Array<ProductGroup>>) => {
						const output: Array<Array<ProductGroup>> = [...group];

						if (output.length > targetMinShelvesNumber) output.length = targetMinShelvesNumber;

						return output;
					}),
				targetMinHangBarsNumber: number = Math.min(
					...planogram.stand.bases
						.map((base: Element) => planogram.stand.availableElements
							.filter((element: Element) => PlanogramLayoutService.filterBaseHangBars(element, base)).length)
				),
				targetHangBars: Array<Array<Element>> = targetBases
					.map((base: Element) =>
						planogram.stand.availableElements
							.filter((element: Element) => PlanogramLayoutService.filterBaseHangBars(element, base))
							.sort((a: Element, b: Element) => a.shapes[0].begin.y - b.shapes[0].begin.y)
					)
					.map((elements: Array<Element>) => {
						elements.length = targetMinHangBarsNumber;
						return elements.filter((element: Element) => element);
					}),
				targetHangBarsTransposed: Array<Array<Element>> = Utils.transposeVariableLengthArray(targetHangBars),
				groupedProductsByTargetHangBars: Array<Array<Array<ProductGroup>>> = originalGroupedProductsByHangBar
					.map((group: Array<Array<ProductGroup>>) => {
						const output: Array<Array<ProductGroup>> = [...group];

						if (output.length > targetMinHangBarsNumber) output.length = targetMinHangBarsNumber;

						return output;
					}),
				targetHooks: Array<Element> = planogram.stand.availableElements
					.filter((element: Element) => element.is(HookElement))
					.sort(PlanogramLayoutService.sortElements),
				targetHoles: Array<Element> = planogram.stand.availableElements
					.filter((element: Element) => element.is(HoleElement))
					.sort(PlanogramLayoutService.sortElements),
				lessThan: number = Math.min(originalGroupedProductsByShelf[0].length, targetMinShelvesNumber),
				lessThanHangBar: number = Math.min(originalGroupedProductsByHangBar[0].length, targetMinHangBarsNumber),
				groupedProductsByShelves: Array<Array<ProductGroup>> = [],
				groupedProductsByHangBars: Array<Array<ProductGroup>> = [],
				hookProductsRatings: { [key: string]: number } = {},
				holeProductsRatings: { [key: string]: number } = {};

			// 3D => 2D
			for (let i = 0; i < lessThan; i++) {
				groupedProductsByShelves.push(
					Utils.to1DArray(
						groupedProductsByTargetShelves.map((group: Array<Array<ProductGroup>>) => group[i] || [])
					)
				);
			}
			for (let i = 0; i < lessThanHangBar; i++) {
				groupedProductsByHangBars.push(
					Utils.to1DArray(
						groupedProductsByTargetHangBars.map((group: Array<Array<ProductGroup>>) => group[i] || [])
					)
				);
			}

			['hook', 'hole'].map((type: string) => {
				const products: Array<ProductGroup> = type == 'hook'
					? originalGroupedProductsByHooks
					: originalGroupedProductsByHoles;

				products.forEach((pg: ProductGroup, idx: number, list: Array<ProductGroup>) => {
					let currentPercentage: number;

					switch (result.type) {
						case 'QTY':
							const productsQty: Array<number> = list
									.map((pg: ProductGroup) => predictedData[pg.product.id]?.qty || 0),
								totalQty: number = productsQty.reduce((a: number, b: number) => a + b, 0);

							currentPercentage = Utils.round(
								(predictedData[pg.product.id]?.qty || 0) / totalQty, 4
							);
							break;

						case 'STOCK_QTY':
							const productsStock: Array<number> = list
									.map((pg: ProductGroup) => predictedData[pg.product.id]?.stock_qty || 0),
								totalStock: number = productsStock.reduce((a: number, b: number) => a + b, 0);

							currentPercentage = Utils.round(
								(predictedData[pg.product.id]?.stock_qty || 0) / totalStock, 4
							);
							break;

						default:
							const productsMeters = type == 'hook' ? hookProductsQty : holeProductsQty,
								totalMeters: number = type == 'hook' ? totalHooksQty : totalHolesQty;

							currentPercentage = Utils.round(
								productsMeters[pg.product.id] / totalMeters, 4
							);
							break;
					}

					if (type == 'hook') {
						hookProductsRatings[pg.product.id] = currentPercentage;
					} else {
						holeProductsRatings[pg.product.id] = currentPercentage;
					}
				});

				const copiedProductsOnElements: { [key: string]: Array<ProductGroup> } = type == 'hook'
					? PlanogramLayoutService
						.checkFreeHooks(hookProductsRatings, originalGroupedProductsByHooks, targetHooks.length)
					: PlanogramLayoutService
						.checkFreeHooks(holeProductsRatings, originalGroupedProductsByHoles, targetHoles.length);

				Array.prototype.push.apply(
					planogram.stand.products,
					Utils.to1DArray(Object.values(copiedProductsOnElements)).map((pg: ProductGroup, idx: number) => {
							const element: Element = type == 'hook' ? targetHooks[idx] : targetHoles[idx];

							pg.qty = 1;
							pg.element = element;
							pg.begin.y = element.getBoundingY().min - pg.ItemHeight;
							pg.begin.x = element.shapes[0].begin.x - (pg.ItemWidth - element.shapes[0].width) / 2;

							return pg;
						}
					)
				);
			});

			groupedProductsByShelves
				.map((shelfProducts: Array<ProductGroup>) => {
					shelfProducts.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y);
					const products: { [key: string]: ProductGroup } = {};

					for (let i = 0; i < shelfProducts.length; i++) {
						if (!shelfProducts[i]) continue;

						const id: number = shelfProducts[i].product.id;

						if (products[id]) continue;

						const agg_products: Array<ProductGroup> = shelfProducts
							.filter((pg: ProductGroup) => pg?.product?.id == id);

						agg_products[0].FacesQty = agg_products.reduce((a: number, b: ProductGroup) => a + b.FacesQty, 0);

						products[id] = agg_products[0];
					}

					return Object.values(products).filter((pg: ProductGroup) => !pg.isLooseForm);
				})
				.forEach((shelfProducts: Array<ProductGroup>, idx: number) => {
					const targetShelves: Array<Element> = targetShelvesTransposed[idx].filter((item: Element) => item),
						productRatings: { [key: string]: number } = {},
						targetShelfProducts: Array<ProductGroup> = [],
						targetSpace: number = targetShelves.reduce((a: number, b: Element) => {
							const width: number = b.shapes[0].type == 'trapeze'
									? b.shapes[0].frontWidth
									: b.shapes[0].width,
								elementsOnShelf: Array<Element> = b.is(ShelfElement)
									? (b as ShelfElement).getElementsOnShelf
									: [],
								sidesWall: Element = elementsOnShelf
									.find((element: Element) => element.parent.type == EquipmentElements.SideWalls),
								partitions: Array<Element> = elementsOnShelf
									.filter((element: Element) => element.parent.type == 'Partition');

							let partitionsWidth: number = 0, sidesWallWidth: number = 0;

							if (sidesWall) {
								sidesWallWidth = sidesWall.shapes.reduce((a: number, b: Figure) => a + b.width, 0);
							}

							if (partitions.length) {
								const sideWallPartitionLeft: Element = partitions
										.find((element: Element) => element.getBoundingX().min == b.getBoundingX().min),
									sideWallPartitionRight: Element = partitions
										.find((element: Element) => element.getBoundingX().max == b.getBoundingX().max);

								if (sideWallPartitionLeft) partitionsWidth += sideWallPartitionLeft.shapes[0].width;
								if (sideWallPartitionRight) partitionsWidth += sideWallPartitionRight.shapes[0].width;
							}

							return a + width - partitionsWidth - sidesWallWidth;
						}, 0);
					const originalWidth: number = shelfProducts
						.filter((pg: ProductGroup) => !pg.dependProduct)
						.reduce((a: number, b: ProductGroup) => Utils.round(a + b.Width), 0);

					shelfProducts
						.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
						.forEach((pg: ProductGroup) => {
							switch (result.type) {
								case 'QTY':
									const productsQty: Array<number> = shelfProducts
											.map((pg: ProductGroup) => predictedData[pg.product.id]?.qty || 0),
										totalQty: number = productsQty.reduce((a: number, b: number) => a + b, 0);

									productRatings[pg.product.id] = Utils.round(
										(predictedData[pg.product.id]?.qty || 0) / totalQty, 4
									);
									break;

								case 'STOCK_QTY':
									const productsStock: Array<number> = shelfProducts
											.map((pg: ProductGroup) => predictedData[pg.product.id]?.stock_qty || 0),
										totalStock: number = productsStock.reduce((a: number, b: number) => a + b, 0);

									productRatings[pg.product.id] = Utils.round(
										(predictedData[pg.product.id]?.stock_qty || 0) / totalStock, 4
									);
									break;

								default:
									productRatings[pg.product.id] = pg.Width / (pg.dependProduct ? pg.dependProduct.Width : originalWidth);
									break;
							}
						});

					shelfProducts
						.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
						.forEach((pg: ProductGroup) => {
							const space: number = pg.dependProduct ? pg.dependProduct.Width : targetSpace;
							pg.FacesQty = Math.floor((space * productRatings[pg.product.id]) / pg.ItemWidth);
							pg.qty = pg.placementDepth * pg.FacesQty;
						});

					let hierarchy: Array<string> = Object.keys(productRatings)
						.sort((a: string, b: string) => productRatings[b] - productRatings[a]);

					if (['STOCK_QTY', 'QTY'].includes(result.type)) {
						hierarchy = Object.keys(productRatings)
							.filter((key: string) => productRatings[key])
							.sort((a: string, b: string) => productRatings[b] - productRatings[a]);
					}

					PlanogramLayoutService.checkShelfFreeSpace(
						shelfProducts.filter((pg: ProductGroup) => hierarchy.includes(String(pg.product.id))),
						targetSpace,
						hierarchy
					);

					let shelfIdx: number = 0, fromX: number = 0;

					shelfProducts
						.filter((pg: ProductGroup) => {
							if (pg.qty <= 0 || isNaN(pg.qty)) {
								const name: string = `${pg.product.article} | ${pg.product.name}`;

								if (!productsErrors[planogram.id].find((i: any) => i.name == name)) {
									if (productRatings[pg.product.id] > 0) {
										productsErrors[planogram.id].push({
											name: name,
											reason: SHARE_ERRORS[result.type] || 'SMART_COPY_FACES_TITLE'
										});
									} else {
										productsErrors[planogram.id].push({
											name: name,
											reason: result.type
										});
									}
								}
							}
							return pg.qty > 0;
						})
						.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y || a.begin.x - b.begin.x)
						.forEach((pg: ProductGroup) => {
							let targetShelf: Element = targetShelves[shelfIdx],
								targetWidth: number = targetShelf.shapes[0].type == 'trapeze'
									? targetShelf.shapes[0].frontWidth
									: targetShelf.shapes[0].width,
								elementsOnShelf: Array<Element> = targetShelf.is(ShelfElement)
									? (targetShelf as ShelfElement).getElementsOnShelf
									: [],
								sidesWall: Element = elementsOnShelf
									.find((element: Element) => element.parent.type == EquipmentElements.SideWalls),
								partitions: Array<Element> = elementsOnShelf
									.filter((element: Element) => element.parent.type == 'Partition'),
								rightSideWall: number = 0,
								siblings: Array<Element> = targetShelf.is(ShelfElement, PalletElement)
									? targetShelf.getSiblingElements
									: [],
								rightSibling: Element = siblings
									.find((s: Element) => Math.abs(s.shapes[0].getBoundingX().min - targetShelf.shapes[0].getBoundingX().max) < 0.01),
								leftSibling: Element = siblings
									.find((s: Element) => Math.abs(targetShelf.shapes[0].begin.x - s.shapes[0].getBoundingX().max) < 0.01),
								shelfMax: number = Utils.max([
									targetShelf.shapes[0].getBoundingX().max,
									...siblings.map((s: Element) => s.shapes[0].getBoundingX().max)
								]),
								sidesWallPartition: Element,
								slicedMax: number;

							if (sidesWall && !fromX) {
								fromX = sidesWall.shapes[0].width;
								rightSideWall = sidesWall.shapes[1].width;
							}

							if (partitions.length && !fromX) {
								sidesWallPartition = partitions
									.find((element: Element) => element.getBoundingX().min == targetShelf.getBoundingX().min);
								if (sidesWallPartition) fromX = sidesWallPartition.shapes[0].width;
							}

							if (!shelfIdx && !fromX && targetShelf.shapes[0].begin.x !== fromX) {
								fromX = targetShelf.shapes[0].begin.x;
							}

							while (shelfIdx < targetShelves.length - 1 && Utils.round(pg.Width + fromX) > Utils.round(shelfMax - rightSideWall) && !pg.dependProduct) {
								const freeSpace: number = Utils.round(shelfMax - rightSideWall - fromX),
									freeFacesQty: number = Math.floor(freeSpace / pg.ItemWidth);

								if (freeFacesQty > 0) {
									const slicedPg: ProductGroup = pg.copy(false, false),
										maxPlacementDepth: number = slicedPg.element.getMaxPlacementDepth(slicedPg);

									slicedPg.element = targetShelf;
									slicedPg.begin.y = targetShelf.shapes[0].begin.y + targetShelf.shapes[0].height + (slicedPg.dependProduct ? slicedPg.dependProduct.getBoundingY().max - slicedPg.dependProduct.element.getBoundingY().max : 0);
									slicedPg.begin.x = fromX;

									const newPlacementDepth: number = slicedPg.element.getMaxPlacementDepth(slicedPg);

									if (slicedPg.placementDepth == maxPlacementDepth || slicedPg.placementDepth > newPlacementDepth) {
										slicedPg.placementDepth = newPlacementDepth;
									}

									slicedPg.FacesQty = freeFacesQty;

									targetShelfProducts.push(slicedPg.copy(false, false));

									pg.FacesQty -= freeFacesQty;
									slicedMax = slicedPg.getBoundingX().max;
									Array.prototype.push.apply(
										planogram.stand.products,
										targetShelfProducts
									);
									targetShelfProducts.length = 0;
								}

								++shelfIdx;
								targetShelf = targetShelves[shelfIdx];
								siblings = targetShelf.getSiblingElements;
								rightSibling = siblings
									.find((s: Element) => Math.abs(s.shapes[0].getBoundingX().min - targetShelf.shapes[0].getBoundingX().max) < 0.01);
								leftSibling = siblings
									.find((s: Element) => Math.abs(targetShelf.shapes[0].begin.x - s.shapes[0].getBoundingX().max) < 0.01);
								targetWidth = targetShelf.shapes[0].type == 'trapeze' ? targetShelf.shapes[0].frontWidth : targetShelf.shapes[0].width;

								if (rightSibling) {
									shelfMax = Utils.max([
										targetShelf.shapes[0].getBoundingX().max,
										...siblings.map((s: Element) => s.shapes[0].getBoundingX().max)
									]);
								} else {
									shelfMax = targetShelf.shapes[0].getBoundingX().max;
								}

								fromX = Utils.round(
									rightSibling && fromX >= targetShelf.shapes[0].begin.x
										? fromX
										: sidesWallPartition
											? sidesWallPartition.shapes[0].width + targetShelf.shapes[0].begin.x
											: sidesWall
												? sidesWall.shapes[0].width + targetShelf.shapes[0].begin.x
												: Math.max(targetShelf.shapes[0].begin.x, fromX),
									3
								);

								if (slicedMax > targetShelf.shapes[0].begin.x) {
									targetShelf = targetShelves.find((element: Element) => element.shapes[0].begin.x > slicedMax);

									if (!targetShelf || pg.dependProduct) return;

									shelfIdx = targetShelves.indexOf(targetShelf);
									fromX = Utils.round(
										sidesWallPartition
											? sidesWallPartition.shapes[0].width + targetShelf.shapes[0].begin.x
											: sidesWall
												? sidesWall.shapes[0].width + targetShelf.shapes[0].begin.x
												: targetShelf.shapes[0].begin.x,
										3
									);
									siblings = targetShelf.getSiblingElements;
									rightSibling = siblings
										.find((s: Element) => Math.abs(s.shapes[0].getBoundingX().min - targetShelf.shapes[0].getBoundingX().max) < 0.01);
									leftSibling = siblings
										.find((s: Element) => Math.abs(targetShelf.shapes[0].begin.x - s.shapes[0].getBoundingX().max) < 0.01);

									if (rightSibling) {
										shelfMax = Utils.max([
											targetShelf.shapes[0].getBoundingX().max,
											...siblings.map((s: Element) => s.shapes[0].getBoundingX().max)
										]);
									} else {
										shelfMax = targetShelf.shapes[0].getBoundingX().max;
									}
								}
							}

							let pgFaces: number = pg.FacesQty;

							if (shelfIdx == targetShelves.length - 1 && Utils.round(pg.Width + fromX) > shelfMax) {
								pgFaces = Math.floor(Utils.round(shelfMax - fromX) / pg.ItemWidth);
							}

							const shelfMaxPoint: number = Utils.round(targetShelf.shapes[0].getBoundingX().max - rightSideWall);

							if (Utils.round(shelfMaxPoint - fromX) < pg.Width && !rightSibling) {
								pgFaces = Math.floor(Utils.round(shelfMaxPoint - fromX) / pg.ItemWidth);
							}

							if ((pgFaces <= 0 && !pg.dependProduct) || (pg.dependProduct && ![...planogram.stand.products, ...targetShelfProducts].find((product: ProductGroup) => product.product.id == pg.dependProduct.product.id))) {
								const name: string = `${pg.product.article} | ${pg.product.name}`;

								if (!productsErrors[planogram.id].find((i: any) => i.name == name)) {
									productsErrors[planogram.id].push({
										name: name,
										reason: 'SMART_COPY_FACES_TITLE'
									});
								}

								return;
							}

							const maxPlacementDepth: number = pg.element.getMaxPlacementDepth(pg);

							pg.element = targetShelf;
							pg.begin.y = targetShelf.shapes[0].begin.y + targetShelf.shapes[0].height + (pg.dependProduct ? pg.dependProduct.getBoundingY().max - pg.dependProduct.element.getBoundingY().max : 0);

							const newPlacementDepth: number = pg.element.getMaxPlacementDepth(pg);

							if (pg.placementDepth == maxPlacementDepth || pg.placementDepth > newPlacementDepth) {
								pg.placementDepth = newPlacementDepth;
							}

							pg.FacesQty = pgFaces;

							if (pg.dependProduct) {
								pg.dependProduct = pg.dependProduct; // to update begin coordinate
								pg.FacesQty = Math.floor((pg.dependProduct.Width * productRatings[pg.product.id]) / pg.ItemWidth) || 1;
								const maxPlacementDepth: number = Math.floor(pg.dependProduct.Depth / pg.ItemDepth);
								if (pg.placementDepth > maxPlacementDepth) pg.placementDepth = maxPlacementDepth;
							} else {
								if (!fromX) {
									pg.begin.x = sidesWall
										? sidesWall.shapes[0].width
										: sidesWallPartition
											? sidesWallPartition[0].shapes[0].width
											: targetShelf.shapes[0].begin.x;
									fromX += pg.begin.x + pg.Width;
								} else if (fromX && shelfIdx && fromX == targetShelf.shapes[0].begin.x && leftSibling) {
									pg.begin.x = leftSibling.products.length
										? Utils.max(leftSibling.products.map((group: Array<ProductGroup>) => group[0].getBoundingX().max))
										: leftSibling.shapes[0].begin.x;
									fromX += pg.Width;
								} else {
									pg.begin.x = fromX;
									if (!pg.dependProduct) fromX += pg.Width;
								}
								fromX = Utils.round(fromX);
							}

							if (pg.qty > 0) targetShelfProducts.push(pg);
						});

					Array.prototype.push.apply(planogram.stand.products, targetShelfProducts);
				});

			groupedProductsByHangBars
				.map((hangBarProducts: Array<ProductGroup>) => {
					const products: { [key: string]: ProductGroup } = {};

					for (let i = 0; i < hangBarProducts.length; i++) {
						if (!hangBarProducts[i]) continue;

						const id: number = hangBarProducts[i].product.id;

						if (products[id]) continue;

						const agg_products: Array<ProductGroup> = hangBarProducts
							.filter((pg: ProductGroup) => pg?.product?.id == id);

						agg_products[0].FacesQty = agg_products.reduce((a: number, b: ProductGroup) => a + b.FacesQty, 0);

						products[id] = agg_products[0];
					}

					return Object.values(products).filter((pg: ProductGroup) => !pg.isLooseForm);
				})
				.forEach((hangBarProducts: Array<ProductGroup>, idx: number) => {
					const targetHangBars: Array<Element> = targetHangBarsTransposed[idx].filter((item: Element) => item),
						productRatings: { [key: string]: number } = {},
						targetHangBarProducts: Array<ProductGroup> = [],
						targetSpace: number = targetHangBars
							.reduce((a: number, b: Element) => Utils.round(a + b.shapes[0].width), 0),
						originalWidth: number = hangBarProducts
							.reduce((a: number, b: ProductGroup) => Utils.round(a + b.Width), 0);

					hangBarProducts.forEach((pg: ProductGroup) => {
						switch (result.type) {
							case 'QTY':
								const productsQty: Array<number> = hangBarProducts
										.map((pg: ProductGroup) => predictedData[pg.product.id]?.qty || 0),
									totalQty: number = productsQty.reduce((a: number, b: number) => a + b, 0);

								productRatings[pg.product.id] = Utils.round(
									(predictedData[pg.product.id]?.qty || 0) / totalQty, 4
								);
								break;

							case 'STOCK_QTY':
								const productsStock: Array<number> = hangBarProducts
										.map((pg: ProductGroup) => predictedData[pg.product.id]?.stock_qty || 0),
									totalStock: number = productsStock.reduce((a: number, b: number) => a + b, 0);

								productRatings[pg.product.id] = Utils.round(
									(predictedData[pg.product.id]?.stock_qty || 0) / totalStock, 4
								);
								break;

							default:
								productRatings[pg.product.id] = pg.Width / (pg.dependProduct
									? pg.dependProduct.Width
									: originalWidth);
								break;
						}
					});

					hangBarProducts.forEach((pg: ProductGroup) => {
						pg.FacesQty = Math.floor((targetSpace * productRatings[pg.product.id]) / pg.ItemWidth);
						pg.qty = pg.placementDepth * pg.FacesQty;
					});

					let hierarchy: Array<string> = Object.keys(productRatings)
						.sort((a: string, b: string) => productRatings[b] - productRatings[a]);

					if (['STOCK_QTY', 'QTY'].includes(result.type)) {
						hierarchy = Object.keys(productRatings)
							.filter((key: string) => productRatings[key])
							.sort((a: string, b: string) => productRatings[b] - productRatings[a]);
					}

					PlanogramLayoutService.checkShelfFreeSpace(
						hangBarProducts.filter((pg: ProductGroup) => hierarchy.includes(String(pg.product.id))),
						targetSpace,
						hierarchy
					);

					let hangBarIdx: number = 0, fromX: number = 0;

					hangBarProducts
						.filter((pg: ProductGroup) => {
							if (pg.qty <= 0 || isNaN(pg.qty)) {
								const name: string = `${pg.product.article} | ${pg.product.name}`;

								if (!productsErrors[planogram.id].find((i: any) => i.name == name)) {
									if (productRatings[pg.product.id] > 0) {
										productsErrors[planogram.id].push({
											name: name,
											reason: SHARE_ERRORS[result.type] || 'SMART_COPY_FACES_TITLE'
										});
									} else {
										productsErrors[planogram.id].push({
											name: name,
											reason: result.type
										});
									}
								}
							}
							return pg.qty > 0;
						})
						.sort((a: ProductGroup, b: ProductGroup) => a.begin.x - b.begin.x)
						.forEach((pg: ProductGroup) => {
							let targetHangBar: Element = targetHangBars[hangBarIdx],
								hangBarMax: number = targetHangBar.shapes[0].getBoundingX().max,
								slicedMax: number;

							if (!hangBarIdx && !fromX && targetHangBar.shapes[0].begin.x !== fromX) {
								fromX = targetHangBar.shapes[0].begin.x;
							}

							while (hangBarIdx < targetHangBars.length - 1 && Utils.round(pg.Width + fromX) > hangBarMax) {
								const freeSpace: number = Utils.round(hangBarMax - fromX),
									freeFacesQty: number = Math.floor(freeSpace / pg.ItemWidth);

								if (freeFacesQty > 0) {
									const slicedPg: ProductGroup = pg.copy(false, false);

									slicedPg.Element = targetHangBar;
									slicedPg.begin.x = fromX;
									slicedPg.FacesQty = freeFacesQty;

									targetHangBarProducts.push(slicedPg);

									pg.FacesQty -= freeFacesQty;
									slicedMax = slicedPg.getBoundingX().max;
									Array.prototype.push.apply(
										planogram.stand.products,
										targetHangBarProducts
									);
									targetHangBarProducts.length = 0;
								}

								++hangBarIdx;
								targetHangBar = targetHangBars[hangBarIdx];
								hangBarMax = targetHangBar.shapes[0].getBoundingX().max;
								fromX = Utils.round(
									fromX >= targetHangBar.shapes[0].begin.x
										? fromX
										: Math.max(targetHangBar.shapes[0].begin.x, fromX),
									3
								);

								if (slicedMax > targetHangBar.shapes[0].begin.x) {
									targetHangBar = targetHangBars.find((element: Element) => element.shapes[0].begin.x > slicedMax);

									if (!targetHangBar) return;

									hangBarIdx = targetHangBars.indexOf(targetHangBar);
									fromX = targetHangBar.shapes[0].begin.x;
									hangBarMax = targetHangBar.shapes[0].getBoundingX().max;
								}
							}

							let pgFaces: number = pg.FacesQty;

							if (hangBarIdx == targetHangBars.length - 1 && Utils.round(pg.Width + fromX) > hangBarMax) {
								pgFaces = Math.floor(Utils.round(hangBarMax - fromX) / pg.ItemWidth);
							}

							const hangBarMaxPoint: number = targetHangBar.shapes[0].getBoundingX().max;

							if (Utils.round(hangBarMaxPoint - fromX) < pg.Width) {
								pgFaces = Math.floor(Utils.round(hangBarMaxPoint - fromX) / pg.ItemWidth);
							}

							if (pgFaces <= 0) {
								const name: string = `${pg.product.article} | ${pg.product.name}`;

								if (!productsErrors[planogram.id].find((i: any) => i.name == name)) {
									productsErrors[planogram.id].push({
										name: name,
										reason: 'SMART_COPY_FACES_TITLE'
									});
								}

								return;
							}

							pg.Element = targetHangBar;
							pg.FacesQty = pgFaces;

							if (!fromX) {
								pg.begin.x = targetHangBar.shapes[0].begin.x;
								fromX = Utils.round(pg.begin.x + pg.Width);
							} else {
								pg.begin.x = fromX;
								fromX = Utils.round(fromX + pg.Width);
							}

							if (pg.qty > 0) targetHangBarProducts.push(pg);
						});

					Array.prototype.push.apply(planogram.stand.products, targetHangBarProducts);
				});

			if (result.isReversed && !planogram.stand.blocks?.length) {
				planogram.stand = this.getReversed(planogram.stand);
			}

			planogram.stand.availableElements.forEach((element: Element) => {
				if (element.is(...NORMALIZABLE)) element.normalize(planogram.stand);
			});

			planogram.stand.products.forEach((pg: ProductGroup, idx: number) => {
				if (pg.error) planogramErrors[planogram.id].push({ shelf: idx });
			});

			const products: Array<IProductConf> = this.productsService.getProductsToConf(planogram.id, planogram.stand.products),
				conf: StandConf = this.planogramsService.getConf(planogram, products, {
					id: planogram.id,
					main_stand: planogram.main_stand,
					blocks: [],
					conf: {
						products: planogramFrom.conf.products.map((p: any) => {
							return { id: p.id, planedPercent: p.planedPercent };
						}),
						reportParams: planogramFrom.reportParams
					}
				});

			configurations.push(conf);
		}

		await lastValueFrom(this.planogramsService.bulkUpdate(configurations));

		await this.planogramsService.makeSvg(this.planogramsService.getDefaultSvgConf(planograms));

		Object.keys(planogramErrors)
			.filter((key: string) => planogramErrors[key].length)
			.forEach((key: string) => {
				errorIndicators.push({
					planogram: Number(key),
					name: 'PLANOGRAM_COPY',
					type: 'ERROR'
				});
			});

		if (errorIndicators.length) this.planogramsService.createIndicators(errorIndicators).subscribe();

		return result.planograms.map((data: { planogram_id: number, plan_id: number }) => {
			const planogram: IPlanogram = planograms.find((p: IPlanogram) => p.main_stand == data.planogram_id);
			return {
				id: planogram.id,
				name: planogram.name,
				errors: productsErrors[planogram.id],
				stand_errors: planogramErrors[planogram.id],
				shop: result.library.find((lib) => lib.planogram_id == data.plan_id)?.planogram_name,
				planogram_id: data.plan_id,
				deniedReversed: false
			};
		});
	}

	public getPlanogramObject(
		info: IPlanogramData,
		products: Array<Product>,
		plan: number,
		draw: boolean = true
	): IPlanogram {
		const planogram: IPlanogram = {
			id: info.id,
			main_stand: info.main_stand,
			planogram_id: plan,
			locked: info.locked,
			name: info.name,
			image: info.image,
			products: signal<Array<Product>>(products),
			blocks: info.blocks,
			stand: new EquipmentBase({
				id: info.instrument.id,
				name: info.instrument.name,
				code: info.instrument.code,
				producer: info.instrument.producer,
				type: info.instrument.type.id,
				type_name: info.instrument.type.name,
				alignment: info.alignment,
				arbitrary: info.arbitrary,
				posMaterials: info.pos_materials,
				textMaterials: info.text_materials,
				products: []
			}),
			conf: info.conf,
			planogram_stand: info.planogram_stand,
            chat: info.chat,
            indicators: info.indicators,
            scheduled_date: info.scheduled_date,
            date: info.date,
            instrument: info.instrument,
			execution: info.execution ? {
				id: info.execution.id,
				sendDate: info.execution.created_at,
				sendUser: info.execution.from_user_full_name,
				enableTimeTracking: info.execution.enable_time_tracking,
				timeTrackingRecord: info.execution.time_execution_record,
				executeUser: info.execution.user_full_name,
				executeDate: info.execution.execution_date,
				deadlineDate: info.execution.deadline_date,
				isOverdue: info.execution.is_overdue,
				wasShow: info.execution.was_show,
				status: info.status
			} : null,
			show_execution_button: info.show_execution_button
		};

		if (draw) {
			planogram.layerObjects = {
				standGroup: new Konva.Group(),
				productGroup: new Konva.Group(),
				detalisationGroup: new Konva.Group(),
				sizesGroup: new Konva.Group(),
				boxGroup: new Konva.Group(),
				tooltipGroup: new Konva.Group(),
				boxNameGroup: new Konva.Group()
			};
			planogram.visible = true;
		}

		this.equipmentService.setEquipmentConfElements(planogram.stand, info.instrument.conf.elements);

		if (info.conf.products) {
			const confIds: Array<number> = info.conf.products.map((p: ProductParams) => p.id),
				productsIds: Array<number> = products.map((p: Product) => p.id);
			products.push(
				...info.conf.products
					.filter((p: ProductParams) => !productsIds.includes(p.id))
					.map((p: ProductParams) => new Product(p))
			);
			products.sort((a: Product, b: Product) => confIds.indexOf(a.id) - confIds.indexOf(b.id));
		}

		if (info.conf.reportParams) planogram.reportParams = info.conf.reportParams;

		info.products
			.sort((a: IPlanogramProductData, b: IPlanogramProductData) => a.position_y - b.position_y)
			.forEach((p: IPlanogramProductData) => {
				const product: Product = products.find((product: Product) => product.id == p.master_product);
				if (!product) return;

				const element: Element = planogram.stand.allElements.find((element: Element) => element.id === p.element_id);
				if (!element) return;

				const productGroup: ProductGroup = new ProductGroup({
					product: product,
					element: element,
					begin: {
						x: p.position_x,
						y: p.position_y,
						z: p.position_z
					},
					qty: p.qty,
					rotation: p.rotation,
					angle: p.angle,
					placementHeight: p.placement_height,
					weight: p.weight,
					selectPacking: p.packing,
					block: p.block,
					height_compression_coef: p.height_compression_coef,
					width_compression_coef: p.width_compression_coef,
					depth_compression_coef: p.depth_compression_coef,
					applied_compressions: p.applied_compressions,
					dependProduct: Utils.getDependProduct(p, planogram.stand.products, element),
					display: draw,
					detailedRotationPoint: this.sessionService.personalSettings.productRotationPoint
				});
				productGroup.placementDepth = p.placement_depth || 1;
				if (!productGroup.element) return;

				planogram.stand.products.push(productGroup);
			});

		if (info.blocks) planogram.stand.setBlocks(info.blocks);

		(planogram.stand as any).orderedElements = Utils.setElementsOrder(planogram.stand.allElements);

		return planogram;
	}

	/** method for mirroring the layout of products */
	public getReversed(originalPlanogram: EquipmentBase): EquipmentBase {
		const planogram: EquipmentBase = originalPlanogram.copy();
		const newElementsMap: Map<ProductGroup, Element> = new Map<ProductGroup, Element>();

		// mirroring for shelves and hangBars
		planogram.allElements
			.filter((element: Element) => element.is(ShelfElement, PalletElement, HangBarElement))
			.forEach((element: ShelfElement | PalletElement | HangBarElement) => {
				if (!element.products?.length) return;

				// write the products in a variable so that all products on the shelf can be changed one by one, without applied changes
				const copyProducts: Array<Array<ProductGroup>> = element.products;

				for (let groupIndex: number = 0; groupIndex < copyProducts.length; groupIndex++) {
					const commonProduct: ProductGroup = copyProducts[groupIndex][0], // the product that is the closest and lowest in the group
						maxX: number = Math.max(...copyProducts[groupIndex].map((pg: ProductGroup) => pg.getBoundingX().max)), // max x coordinate in group
						siblingSegments: Array<Bounding> = element.getSiblingSegments(); // coordinates of all adjacent segments
					if (!siblingSegments?.length) break;

					const deltaX: number = commonProduct.begin.x - maxX, // difference between begin.x and max group x
						x: number = Utils.round(
							(siblingSegments[siblingSegments.length - 1].max + siblingSegments[0].min) -
							commonProduct.getBoundingX().min +
							deltaX,
							3);

					for (let productIndex: number = 0; productIndex < copyProducts[groupIndex].length; productIndex++) {
						const product: ProductGroup = copyProducts[groupIndex][productIndex];
						product.begin.x = x;
						if (commonProduct.angle) product.rotateAroundCenter(-commonProduct.angle);

						let productElement: Element;
						const siblingElements: Array<Element> = [element, ...element.getSiblingElements]
							.sort((a: Element, b: Element) => a.getBoundingX().min - b.getBoundingX().min);
						if (x <= 0) {
							productElement = siblingElements[0];
						} else {
							if (x >= siblingSegments[siblingSegments.length - 1].max) {
								productElement = siblingElements[siblingElements.length - 1];
							} else {
								productElement = siblingElements.find((e) => e.getBoundingX().min < x && e.getBoundingX().max > x)
							}
						}
						newElementsMap.set(product, productElement || element);

						// keep the depth if it is for the entire shelf
						if (element.getMaxPlacementDepth(product) === product.placementDepth) {
							const oldFacesQty: number = product.FacesQty;
							product.placementDepth = (productElement || element).getMaxPlacementDepth(product);
							product.qty = product.placementDepth * oldFacesQty;
						}
					}
				}
			});

		newElementsMap.forEach((element: Element, pg: ProductGroup) => pg.Element = element);

		// mirroring for hook, hole
		planogram.allElements
			.filter((element: Element) => ['HOOK_PANEL', 'PERFORATION', 'HOOK_WALL', 'PERFORATION_WALL'].includes(element.parent.name))
			.forEach((element: Element) => {
				const rows: Array<Array<Element>> = planogram.getElementsRows(element);

				for (let rowIndex: number = 0; rowIndex < rows.length; rowIndex++) {
					const row: Array<Element> = rows[rowIndex];

					for (let elementIndex: number = 0; elementIndex < Math.floor(row.length / 2); elementIndex++) {
						const newElement: Element = row[row.length - elementIndex - 1],
							newElementProducts: Array<Array<ProductGroup>> = newElement.products,
							el: Element = row[elementIndex],
							copyProducts: Array<Array<ProductGroup>> = el.products;

						PlanogramLayoutService.moveHookProducts(el, newElement, copyProducts);
						PlanogramLayoutService.moveHookProducts(newElement, el, newElementProducts);
					}
				}
			});

		// elements that fall into siblings will be ignored, so that mirroring does not work on the same boxes several times
		const ignoredElements: Array<Element> = [];

		// mirroring for boxes
		planogram.allElements
			.filter((element: Element) => planogram.allElements
				.filter((el: Element) => el instanceof BoxElement)
				.some((el: Element) => el.depend === element))
			.forEach((element: Element) => {
				if (ignoredElements.includes(element)) return;
				ignoredElements.push(...element.getSiblingElements);

				const rows: Array<Array<Element>> = planogram.getBoxesRows(element);

				for (let rowIndex: number = 0; rowIndex < rows.length; rowIndex++) {
					const row: Array<Element> = rows[rowIndex];

					for (let elementIndex: number = 0; elementIndex < Math.ceil(row.length / 2); elementIndex++) {
						const newElement: Element = row[row.length - elementIndex - 1],
							newElementProducts: Array<Array<ProductGroup>> = newElement.products,
							el: Element = row[elementIndex],
							copyProducts: Array<Array<ProductGroup>> = el.products;

						PlanogramLayoutService.moveBoxProducts(el, newElement, copyProducts);
						if (el !== newElement) PlanogramLayoutService.moveBoxProducts(newElement, el, newElementProducts);
					}
				}
			});

		return planogram;
	}

	public async makeCopying(
		planograms: Array<IPlanogram>,
		planogramFrom: IPlanogram,
		result: CopyingData
	): Promise<Array<CopyingNotification>> {
		if (!result.type) {
			return await this.copyPlanogramSimple(planograms, planogramFrom, result);
		} else {
			return await this.copyPlanogramSmart(planograms, planogramFrom, result);
		}
	}

	public async makeRotation(
		data: Array<IPlanogramData>,
		plans: Array<IPlanData>,
		changedProducts: Array<RotationData>
	): Promise<Array<RotationNotification>> {
		const productsRotateTo: Array<number> = changedProducts.map((p: RotationData) => p.product_rotate_to.id),
			planogramConfigurations: Map<IPlanogram, StandConf> = new Map<IPlanogram, StandConf>(),
			planogramErrors: { [key: string]: Array<any> } = {},
			rotationMessages: Array<any> = [],
			rotationNotifications: Array<RotationNotification> = [],
			errorIndicators: Array<any> = [],
			planIds: Array<number> = plans.map((plan: IPlanData) => plan.id);

		data.forEach((item: IPlanogramData) => {
			const planId: number = item.planogram_stand[0].planogram_id,
				rotationConfiguration: RotationConfiguration = this.rotateProducts(item, changedProducts),
				planogram: IPlanogram = this.getPlanogramObject(item, [], planId, false);

			planogram.planogram_stand.forEach((ps: IPlanogramPlanData) => {
				if (planIds.includes(ps.planogram_id)) {
					rotationNotifications.push({
						id: planogram.main_stand,
						name: planogram.name,
						shop: plans.find((p: IPlanData) => p.id == ps.planogram_id),
						conf: rotationConfiguration
					});
				}
			});

			planogram.stand.allElements.forEach((element: Element) => {
				if (element.is(...NORMALIZABLE)) element.normalize(planogram.stand);
			});

			planogramErrors[planogram.main_stand] = [];

			planogram.stand.products.forEach((pg: ProductGroup, idx: number) => {
				if (pg.error) planogramErrors[planogram.main_stand].push({ shelf: idx });
				if (productsRotateTo.includes(pg.product.id)) pg.selectPacking = null;
			});

			if (!planogramConfigurations.has(planogram)) {
				if (rotationConfiguration.errors.notFound.length !== changedProducts.length) {
					const products: Array<IProductConf> = this.productsService.getProductsToConf(planogram.main_stand, planogram.stand.products),
						conf: StandConf = this.planogramsService.getConf(planogram, products, {
							id: planogram.id,
							main_stand: planogram.main_stand,
							conf: {
								products: (planogram.conf?.products || []).map((p: any) => {
									return { id: p.id, planedPercent: p.planedPercent };
								})
							}
						}),
						productIds: Array<number> = conf.products.map((p: IProductConf) => p.master_product);

					if (conf.conf.products) {
						conf.conf.products.forEach((p: any) => {
							p.planedPercent = productIds.includes(p.id) ? Utils.round(100 / productIds.length) : 0;
						});
					}

					planogramConfigurations.set(planogram, conf);
				}

				rotationMessages.push({
					user: this.sessionService.defaults.user_id,
					stand: planogram.main_stand,
					body: 'USER_ROTATED_PRODUCT',
					conf: rotationConfiguration,
					type: 'rotation_message'
				});
			}
		});

		await lastValueFrom(this.planogramsService.bulkUpdate(Array.from(planogramConfigurations.values())));
		await lastValueFrom(this.layoutService.bulkSendRotationMessages(rotationMessages));
		await this.planogramsService.makeSvg(this.planogramsService.getDefaultSvgConf(Array.from(planogramConfigurations.keys())));

		rotationNotifications
			.filter((n: RotationNotification) => planogramErrors[n.id]?.length)
			.forEach((n: RotationNotification) => n.conf.errors.stand = true);

		Object.keys(planogramErrors)
			.filter((key: string) => planogramErrors[key].length)
			.forEach((key: string) => {
				errorIndicators.push({
					planogram: Number(key),
					name: 'PRODUCT_ROTATION',
					type: 'ERROR'
				});
			});

		if (errorIndicators.length) this.planogramsService.createIndicators(errorIndicators).subscribe();

		return rotationNotifications;
	}

	public rotateProducts(planogram: IPlanogramData, changedProducts: Array<RotationData>): RotationConfiguration {
		const productsRotateFrom: Array<number> = changedProducts.map((p: RotationData) => p.product_rotate_from.id),
			planogramProducts: Array<number> = planogram.products.map((p: IPlanogramProductData) => p.master_product),
			rotationConfiguration: RotationConfiguration = {
				data: [],
				errors: {
					block: [],
					weight: [],
					notFound: [],
					stand: false
				}
			};

		rotationConfiguration.errors.notFound = changedProducts
			.filter((p: RotationData) => !planogramProducts.includes(p.product_rotate_from.id));

		planogram.products
			.sort((a: IPlanogramProductData, b: IPlanogramProductData) => a.position_y - b.position_y)
			.filter((standProduct: IPlanogramProductData) => productsRotateFrom.includes(standProduct.master_product))
			.forEach((standProduct: IPlanogramProductData) => {
				const product: RotationData = changedProducts
						.find((p: RotationData) => p.product_rotate_from.id == standProduct.master_product),
					productRotateTo: Product = product.product_rotate_to,
					productRotateFrom: Product = product.product_rotate_from,
					standProductRotateFrom: IPlanogramProductData = planogram.products
						.find((p: IPlanogramProductData) => p.master_product == productRotateFrom.id),
					sameProductOnShelf: IPlanogramProductData = planogram.products
						.find((p: IPlanogramProductData) => p.master_product == productRotateTo.id && p.element_id == standProduct.element_id),
					block: IPlanogramBlockData = planogram.blocks
						.find((b: IPlanogramBlockData) => b.id == standProduct.block);

				// add product to left panel
				if (!planogram.conf.products.map((p: ProductParams) => p.id).includes(productRotateTo.id)) {
					planogram.conf.products.push({
						id: productRotateTo.id,
						name: productRotateTo.name,
						photo: productRotateTo.photo,
						article: productRotateTo.article,
						width: productRotateTo.width,
						height: productRotateTo.height,
						depth: productRotateTo.depth,
						packings: productRotateTo.packings,
						is_loose: productRotateTo.is_loose,
						is_active: productRotateTo.is_active,
						is_new: productRotateTo.is_new,
						brands: productRotateTo.brands,
						assortment_type: productRotateTo.assortment_type,
						statuses: productRotateTo.statuses,
						categories: productRotateTo.categories,
						suppliers: productRotateTo.suppliers,
						markers: productRotateTo.markers,
						planedPercent: productRotateTo.planedPercent,
						stock_qty: productRotateTo.stock_qty,
						sales_qty: productRotateTo.sales_qty,
						is_master_position: productRotateTo.is_master_position
					});
				}

				// product is weight and it cannot be rotated
				if (productRotateTo.is_loose && !Utils.rotateInBoxes(standProduct, planogram)) {
					if (!rotationConfiguration.errors.weight.includes(product)) {
						rotationConfiguration.errors.weight.push(product);
					}
					return;
				}

				// can't rotate product in block
				if (block && !Utils.productIsRelevantForBlock(productRotateTo, block)) {
					if (!rotationConfiguration.errors.block.includes(product)) {
						rotationConfiguration.errors.block.push(product);
						return;
					}
				}

				// rotation on hooks
				if (Utils.rotateOnHooks(standProduct, planogram)) {
					if (sameProductOnShelf) {
						sameProductOnShelf.placement_depth += standProduct.placement_depth;
						sameProductOnShelf.qty = sameProductOnShelf.placement_depth;
						planogram.products = planogram.products
							.filter((product: IPlanogramProductData) => product !== standProduct);
					} else {
						standProduct = Utils.rotateProductOnHook(standProduct, productRotateFrom, productRotateTo, planogram);
					}

					if (!rotationConfiguration.data.includes(product)) {
						rotationConfiguration.data.push(product);
					}
					return;
				}

				// rotation on holes
				if (Utils.rotateOnHoles(standProduct, planogram)) {
					standProduct = Utils.rotateProductOnHole(standProduct, productRotateFrom, productRotateTo, planogram);
					return;
				}

				// can rotate
				if (sameProductOnShelf) {
					const oldDepth: number = standProduct.placement_depth,
						productWidth: number = Utils.getProductRotateToWidth(standProductRotateFrom, productRotateFrom, oldDepth),
						productDepth: number = Utils.getProductRotateToDepth(standProduct, productRotateTo, productRotateFrom),
						productHeight: number = Utils.getProductRotateToHeight(productRotateFrom, standProduct),
						findPacking: IProductPacking = productRotateTo.packings
							.find((packing: IProductPacking) => packing.id === sameProductOnShelf.packing),
						faces: number = standProductRotateFrom.rotation == ProductGroupRotation.Side
							? Math.floor(productWidth / (findPacking ? findPacking.depth : productRotateTo.depth)) || 1
							: [ProductGroupRotation.FrontRotatedRight, ProductGroupRotation.FrontRotatedLeft].includes(standProductRotateFrom.rotation)
								? Math.floor(productWidth / (findPacking ? findPacking.height : productRotateTo.height)) || 1
								: Math.floor(productWidth / (findPacking ? findPacking.width : productRotateTo.width)) || 1,
						placementDepth: number = productDepth || 1,
						placementHeight: number = Math.floor(productHeight / productRotateTo.height) || 1;

					if (sameProductOnShelf.master_product == standProduct.depend_product) {
						sameProductOnShelf.placement_height += placementHeight;
					} else if (
						sameProductOnShelf.position_x == standProduct.position_x &&
						sameProductOnShelf.position_y == standProduct.position_y
					) {
						sameProductOnShelf.placement_depth += placementDepth;
						sameProductOnShelf.qty += placementDepth;
					} else {
						sameProductOnShelf.qty += faces * sameProductOnShelf.placement_depth;
					}

					planogram.products = planogram.products
						.filter((product: IPlanogramProductData) => product !== standProduct);
				} else {
					const dependProductIdx: number = planogram.products.findIndex(
						(p: IPlanogramProductData) => p.depend_product == standProduct.master_product && p.element_id == standProduct.element_id
					);

					if (dependProductIdx > -1) planogram.products[dependProductIdx].depend_product = productRotateTo.id;

					standProduct = Utils.rotateProducts(
						standProduct,
						productRotateTo,
						productRotateFrom,
						standProductRotateFrom,
						planogram.instrument
					);
				}

				if (!rotationConfiguration.data.includes(product)) {
					rotationConfiguration.data.push(product);
				}
			});

		const replace = (item: RotationData): RotationConfigurationData => {
			return {
				rotate_from: item.rotate_from,
				rotate_to: item.rotate_to,
				product_rotate_from: {
					id: item.product_rotate_from.id,
					article: item.product_rotate_from.article,
					name: item.product_rotate_from.name
				},
				product_rotate_to: {
					id: item.product_rotate_to.id,
					article: item.product_rotate_to.article,
					name: item.product_rotate_to.name
				},
				errors: item.errors
			}
		}
		rotationConfiguration.data = rotationConfiguration.data.map((item: RotationData) => replace(item));
		rotationConfiguration.errors.block = rotationConfiguration.errors.block.map((item: RotationData) => replace(item));
		rotationConfiguration.errors.weight = rotationConfiguration.errors.weight.map((item: RotationData) => replace(item));
		rotationConfiguration.errors.notFound = rotationConfiguration.errors.notFound.map((item: RotationData) => replace(item));

		return rotationConfiguration;
	}

	public setDetailedVariables(planogram: IPlanogram, detailedElement: Element): void {
		const basesList: Array<EquipmentElements> = [EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze];
		const shelvesList: Array<EquipmentElements> = [EquipmentElements.Shelf, EquipmentElements.AngleShelf];
		const backWallsList: Array<EquipmentElements> = [
			EquipmentElements.BackWall,
			EquipmentElements.CentralPillar,
			EquipmentElements.HookWall,
			EquipmentElements.PerforatedWall,
			EquipmentElements.Showcase
		];

		if (detailedElement.parent.is(...basesList, ...shelvesList)) {
			const sortedShelves: Array<Element> = [];

			if (detailedElement.parent.is(...basesList)) {
				const backWall: Element = planogram.stand.allElements.find((el: Element) => {
					return backWallsList.includes(el.parent.type) && el.depend.id === detailedElement.id;
				});

				sortedShelves.push(
					detailedElement,
					...planogram.stand.allElements
						.filter((el: Element) => shelvesList.includes(el.parent.type) && el.depend?.id === backWall?.id)
						.sort((a: Element, b: Element) => {
							const ySort: number = Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
							const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

							return ySort || xSort;
						})
				);
			}

			if (detailedElement.parent.is(...shelvesList)) {
				const backWall: Element = detailedElement.depend;
				const base: Element = backWall.depend;

				sortedShelves.push(
					base,
					...planogram.stand.allElements
						.filter((el: Element) => shelvesList.includes(el.parent.type) && el.depend.id === backWall.id)
						.sort((a: Element, b: Element) => {
							const ySort: number = Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
							const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

							return ySort || xSort;
						})
				);
			}

			const availableShelves: Array<Element> = sortedShelves.filter((el: Element) => {
				return !el.hasExtension(ExtensionType.Availability) || el.isExtensionActive(ExtensionType.Availability);
			});

			const currentIndex: number = sortedShelves.findIndex((el: Element) => el === detailedElement);

			planogram.detailedElementNumber = availableShelves.findIndex((el: Element) => el === detailedElement) + 1;
			planogram.detailedElementNext = currentIndex + 1 < sortedShelves.length ? sortedShelves[currentIndex + 1] : null;
			planogram.detailedElementPrev = currentIndex - 1 >= 0 ? sortedShelves[currentIndex - 1] : null;
		}

		if (detailedElement.parent.type == EquipmentElements.Hook) {
			const parent: Element = detailedElement.depend;
			const hooks: Array<Element> = planogram.stand.allElements
				.filter((el: Element) => el.depend?.id === parent.id)
				.sort((a: Element, b: Element) => {
					const ySort: number = Utils.round(b.shapes[0].begin.y) - Utils.round(a.shapes[0].begin.y);
					const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

					return ySort || xSort;
				});

			const availableHooks: Array<Element> = hooks.filter((el: Element) => {
				return !el.hasExtension(ExtensionType.Availability) || el.isExtensionActive(ExtensionType.Availability);
			});

			const currentIndex: number = hooks.findIndex((el: Element) => el === detailedElement);

			planogram.detailedElementNumber = availableHooks.findIndex((el: Element) => el === detailedElement) + 1;
			planogram.detailedElementNext = currentIndex + 1 < hooks.length ? hooks[currentIndex + 1] : null;
			planogram.detailedElementPrev = currentIndex - 1 >= 0 ? hooks[currentIndex - 1] : null;
		}
	}
}
